import { Activity, useMemo } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { SegmentedControl } from "@similarweb/ui-components";
import { Flex, Stack } from "@similarweb/ui-components/foundations";

import { abbrNumberFilter } from "app/filters/ngFilters";

import { getHasAdsIntelPackage } from "../ads-alerts/utils/getHasAdsIntelPackage";
import { AdsPanel } from "../tab-panels/AdsPanel";
import { NewsPanel } from "../tab-panels/NewsPanel";
import { SeoPanel } from "../tab-panels/SeoPanel";
import { SocialPanel } from "../tab-panels/SocialPanel";
import { WebsiteContentPanel } from "../tab-panels/WebsiteContentPanel";

import { useAlertCounts } from "./useAlertCounts";
import { getAvailableAlertTabs } from "./config";
import { useTabFromUrl } from "./useTabFromUrl";
import { useViewModeFromUrl } from "./useViewModeFromUrl";
import { ViewModeToggle } from "./ViewModeToggle";

export const AlertsTabs = () => {
    const { translate } = useTranslate();
    const hasAdsIntelPackage = getHasAdsIntelPackage();
    const availableTabs = getAvailableAlertTabs(hasAdsIntelPackage);
    const { currentTab, tabIndex, handleTabChange } = useTabFromUrl(availableTabs);
    const { currentViewMode, handleViewModeChange } = useViewModeFromUrl(currentTab);
    const { data: alertCounts, isLoading: isCountsLoading } = useAlertCounts();
    const segmentedControlValues = useMemo(
        () =>
            availableTabs.map((tab) => ({
                label: translate(tab.translationKey, {
                    count: isCountsLoading ? "..." : `${abbrNumberFilter()(alertCounts?.[tab.id])}`,
                }),
                value: tab.id,
            })),
        [availableTabs, translate, alertCounts, isCountsLoading],
    );

    const handleChange = (tab: number) => {
        handleTabChange(tab);
    };

    return (
        <Stack
            gap="16px"
            flexGrow={1}
        >
            <Flex
                justifyContent="space-between"
                alignItems="center"
            >
                <SegmentedControl
                    values={segmentedControlValues}
                    selectedIndex={tabIndex}
                    onChange={handleChange}
                />
                <ViewModeToggle
                    currentTab={currentTab}
                    onViewModeChange={handleViewModeChange}
                    currentViewMode={currentViewMode}
                />
            </Flex>

            <Activity
                key="news"
                mode={currentTab === "news" ? "visible" : "hidden"}
            >
                <NewsPanel viewMode={currentViewMode} />
            </Activity>

            <Activity
                key="social"
                mode={currentTab === "socialMedia" ? "visible" : "hidden"}
            >
                <SocialPanel viewMode={currentViewMode} />
            </Activity>

            {hasAdsIntelPackage ? (
                <Activity
                    key="ads"
                    mode={currentTab === "ads" ? "visible" : "hidden"}
                >
                    <AdsPanel viewMode={currentViewMode} />
                </Activity>
            ) : null}

            <Activity
                key="content"
                mode={currentTab === "websiteContent" ? "visible" : "hidden"}
            >
                <WebsiteContentPanel viewMode={currentViewMode} />
            </Activity>

            <Activity
                key="seo"
                mode={currentTab === "seoAlerts" ? "visible" : "hidden"}
            >
                <SeoPanel />
            </Activity>
        </Stack>
    );
};
